import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { Skeleton, MultiSelectFilter } from '@org/ui';
import { ClassMosaicCard } from '../../../components/class-mosaic-card';
import { ClassFilterToggle, type ClassFilter } from '../../../components/class-filter-toggle';
import { ClassKindToggle } from '../../../components/class-kind-toggle';
import { ClassSearchInput } from '../../../components/class-search-input';
import { useClassKindFilter } from '../../../lib/class-kind-filter';
import { useDebouncedValue } from '../../../lib/use-debounced-value';
import { LiveNowBanner } from '../../../components/live-now-banner';

type SearchParams = { filter: ClassFilter; categories?: string[]; q?: string };

const VALID_FILTERS = new Set<ClassFilter>(['past', 'upcoming', 'all']);

export const Route = createFileRoute('/_app/classes/')({
  component: ClassesPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const raw = typeof search.filter === 'string' ? (search.filter as ClassFilter) : 'past';
    const filter = VALID_FILTERS.has(raw) ? raw : 'past';
    const rawCategories = search.categories;
    const categories = Array.isArray(rawCategories)
      ? rawCategories.filter((c): c is string => typeof c === 'string' && c.length > 0)
      : typeof rawCategories === 'string' && rawCategories.length > 0
        ? rawCategories.split(',')
        : [];
    const q = typeof search.q === 'string' ? search.q.trim().slice(0, 100) : '';
    return {
      filter,
      ...(categories.length > 0 ? { categories } : {}),
      ...(q ? { q } : {}),
    };
  },
});

function ClassesPage() {
  const { t } = useTranslation('admin');
  const { filter, categories, q } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  // The "lupa": typed text is local, and lands in the URL (?q=) after a short
  // pause so the search survives refresh / back navigation.
  const [searchText, setSearchText] = useState(q ?? '');
  const debouncedSearch = useDebouncedValue(searchText).trim();
  useEffect(() => {
    if (debouncedSearch === (q ?? '')) return;
    navigate({
      search: (prev) => ({ ...prev, q: debouncedSearch || undefined }),
      replace: true,
    });
  }, [debouncedSearch, q, navigate]);
  const activeSearch = q ?? '';
  // "Aulas regulares | Todas as aulas", remembered per device; null while the
  // student's cohort is unknown (queries wait, see lib/class-kind-filter.ts).
  const { kind, setKind, showToggle: showKindToggle } = useClassKindFilter();
  const kindReady = kind !== null;
  const queryKind = kind ?? 'all';

  // Stable reference for the query key — empty array would otherwise create a new ref each render.
  const selectedCategories = useMemo(() => categories ?? [], [categories]);
  const listInput = useMemo(
    () => ({
      filter,
      kind: queryKind,
      ...(selectedCategories.length > 0 ? { categories: selectedCategories } : {}),
      ...(activeSearch ? { search: activeSearch } : {}),
    }),
    [filter, queryKind, selectedCategories, activeSearch],
  );
  const countsInput = useMemo(
    () => ({
      kind: queryKind,
      ...(selectedCategories.length > 0 ? { categories: selectedCategories } : {}),
      ...(activeSearch ? { search: activeSearch } : {}),
    }),
    [queryKind, selectedCategories, activeSearch],
  );

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = trpc.classes.getStudentClassesList.useInfiniteQuery(
    listInput,
    { getNextPageParam: (last) => last.nextCursor ?? undefined, enabled: kindReady },
  );

  // Counts live in their own query so toggling past/upcoming/all doesn't blank them out.
  // They refetch only when the event-kind filter changes.
  const { data: countsData } = trpc.classes.getStudentClassesCounts.useQuery(countsInput, {
    keepPreviousData: true,
    enabled: kindReady,
  });

  // Category filter options are sourced dynamically from the distinct
  // event_categories.name values the student has access to (within the chosen kind).
  const { data: categoryNames } = trpc.classes.getStudentClassCategories.useQuery(
    { kind: queryKind },
    { enabled: kindReady },
  );

  // Live "happening now" item is sourced from a tiny separate query — it should
  // appear regardless of which filter is selected, so we can't rely on `data`.
  const { data: liveData } = trpc.classes.getDashboardClasses.useQuery();
  const liveCard = useMemo(() => {
    if (!liveData) return null;
    return liveData.find((c) => c.isLive || c.isSoon) ?? null;
  }, [liveData]);

  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const counts = countsData ?? { past: 0, upcoming: 0, all: 0 };

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) fetchNextPage();
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage]);

  const handleFilterChange = (next: ClassFilter) => {
    navigate({
      search: (prev) => ({ ...prev, filter: next }),
      replace: true,
    });
  };

  const handleCategoriesChange = (next: string[]) => {
    navigate({
      search: (prev) => ({
        ...prev,
        categories: next.length > 0 ? next : undefined,
      }),
      replace: true,
    });
  };

  const categoryOptions = useMemo(
    () => (categoryNames ?? []).map((name) => ({ value: name, label: name })),
    [categoryNames],
  );

  const emptyKey = filter;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-10 lg:py-12">
      <header className="space-y-2">
        <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">
          {t('p2e.classes.page.eyebrow')}
        </p>
        <h1 className="text-3xl font-semibold text-gray-900 sm:text-4xl">
          {t('p2e.classes.page.title')}
        </h1>
      </header>

      {liveCard && (
        <div className="!mt-6">
          <LiveNowBanner
            classId={liveCard.classId}
            name={liveCard.name}
            eventStartTime={liveCard.eventStartTime}
            eventLocation={liveCard.eventLocation}
            isLive={liveCard.isLive}
            completedBlocks={liveCard.completedBlocks ?? 0}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <ClassSearchInput value={searchText} onChange={setSearchText} />
        {showKindToggle && kind && <ClassKindToggle value={kind} onChange={setKind} />}
        <ClassFilterToggle value={filter} onChange={handleFilterChange} counts={counts} />
        <MultiSelectFilter
          label={t('p2e.classes.page.categoryFilter.label')}
          clearLabel={t('p2e.classes.page.categoryFilter.clear')}
          options={categoryOptions}
          value={selectedCategories}
          onChange={handleCategoriesChange}
          triggerClassName="py-3 border-rose-200 text-gray-700 hover:bg-rose-50 hover:text-[#cf2e2e] focus-visible:ring-rose-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_2px_0_rgba(0,0,0,0.04),0_4px_8px_rgba(247,141,167,0.08)]"
          badgeClassName="bg-[#cf2e2e]"
          contentClassName="border-rose-200"
          itemActiveClassName="bg-rose-50 text-[#cf2e2e]"
        />
      </div>

      {isLoading || !kindReady ? (
        <MosaicSkeletons />
      ) : items.length === 0 ? (
        <EmptyState
          title={t(activeSearch ? 'p2e.classes.page.search.emptyTitle' : `p2e.classes.page.empty.${emptyKey}.title`)}
          description={t(
            activeSearch ? 'p2e.classes.page.search.emptyDescription' : `p2e.classes.page.empty.${emptyKey}.description`,
          )}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((item) => (
              <ClassMosaicCard
                key={`${item.classId}-${item.eventId}-${item.eventStartTime}`}
                classId={item.classId}
                name={item.name}
                eventTitle={item.eventTitle}
                eventStartTime={item.eventStartTime}
                eventLocation={item.eventLocation}
                imageUrl={item.imageUrl}
                isPast={item.isPast}
                isToday={item.isToday}
                isLive={item.isLive}
                isSoon={item.isSoon}
                completedBlocks={item.completedBlocks ?? 0}
                totalBlocks={item.totalBlocks ?? 0}
              />
            ))}
          </div>

          <div ref={sentinelRef} className="h-12" aria-hidden="true" />

          {isFetchingNextPage && (
            <p className="text-center text-sm text-gray-500">{t('p2e.classes.page.loadMore')}</p>
          )}
        </>
      )}
    </div>
  );
}

function MosaicSkeletons() {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, idx) => (
        <Skeleton key={idx} className="aspect-[3/4] w-full rounded-xl bg-rose-100/60" />
      ))}
    </div>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-[28px] border border-dashed border-rose-200 bg-[#fff6f9] p-12 text-center">
      <h2 className="text-2xl font-semibold text-gray-900">{title}</h2>
      <p className="mt-2 text-gray-600">{description}</p>
    </div>
  );
}
